/* eslint react-hooks/exhaustive-deps: 0 */
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

const AREAS = [
  "entity_and_environment", "industry_regulatory",
  "accounting_systems_records", "framework_and_policies",
];

export default function W172UnderstandingPage() {
  const { current, currentEngId } = useEngagement();
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [newArea, setNewArea] = useState("");

  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/understanding`, { _silent: true });
    setState(r.data); setDraft(r.data?.areas || {});
  };
  useEffect(() => { reload(); }, [currentEngId]);

  const save = async () => {
    try {
      await api.post(`/engagements/${currentEngId}/w17/understanding`, draft, { _silent: true });
      await reload();
      toast.success(t("w17_2.savedOk"));
    } catch(e){ toast.error(toErrMsg(e, "Failed")); }
  };
  const addArea = async () => {
    const t2 = (newArea||"").trim();
    if (!t2) return;
    try {
      await api.post(`/engagements/${currentEngId}/w17/understanding/likely-areas`, { text: t2 }, { _silent: true });
      setNewArea("");
      await reload();
    } catch(e){ toast.error(toErrMsg(e, t("w17_2.likelyAreaInvalid"))); }
  };
  const removeArea = async (id) => {
    try {
      await api.delete(`/engagements/${currentEngId}/w17/understanding/likely-areas/${id}`, { _silent: true });
      await reload();
    } catch(e){ toast.error(toErrMsg(e, "Failed")); }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w17-2-loading">{t("common.loading")}</div>;
  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return (
    <div className="p-6 text-sm text-muted-foreground" data-testid="w17-2-audit-only">
      {t("w17_2.notForAudit")}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="w17-2-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-2-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">502(r)</span>
          {t("w17_2.title")}
        </h1>
        <SignOffBar itemType="w17_2" itemKey="W17.2" />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_2.subtitle")}</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_2.areasTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {AREAS.map(a => (
            <div key={a} data-testid={`w17-2-area-${a}`}>
              <div className="text-[12px] font-medium mb-1">{t(`w17_2.areas.${a}`)}</div>
              <Textarea value={draft[a] || ""}
                             onChange={e => setDraft({...draft, [a]: e.target.value})}
                             placeholder={t(`w17_2.areas.${a}_ph`)}
                             className="h-20 text-[12px]"
                             data-testid={`w17-2-area-${a}-input`} />
            </div>
          ))}
          <Button onClick={save} data-testid="w17-2-save">{t("w17_2.save")}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base normal-case" data-testid="w17-2-likely-title">
            {t("w17_2.likelyTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex gap-2">
            <Input value={newArea} onChange={e=>setNewArea(e.target.value)}
                       placeholder={t("w17_2.likelyPh")} className="h-8 text-[12px]"
                       data-testid="w17-2-likely-input" />
            <Button onClick={addArea} size="sm" data-testid="w17-2-likely-add">
              <Plus className="h-3 w-3 me-1" />{t("w17_2.add")}
            </Button>
          </div>
          <ul className="space-y-1 text-[12px]">
            {(state.likely_areas||[]).map(a => (
              <li key={a.id} className="flex items-start justify-between gap-2 rounded border px-2 py-1"
                    data-testid={`w17-2-likely-${a.id}`}>
                <div>
                  <div>{a.text}</div>
                  <div className="text-[10px] text-muted-foreground">{a.by_name || "—"} · {(a.at||"").slice(0,10)}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={()=>removeArea(a.id)}
                            data-testid={`w17-2-likely-${a.id}-del`}>
                  <Trash2 className="h-3 w-3 text-rose-700" />
                </Button>
              </li>
            ))}
            {(!state.likely_areas || state.likely_areas.length===0) && (
              <li className="text-muted-foreground" data-testid="w17-2-likely-empty">{t("w17_2.noLikelyAreas")}</li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
